import { AuthType } from '@/const/auth';
import { ActionType, AssetType, ChangeMediaOperationType, ChangeMediaType, ContentType, EventType, MissionType, SenderType } from '@/const/mission';
import { AuditStatusDetailType, AuditStatusType, BotAuditStatusDetailType, PayAuditStatusType } from '@/const/status';
import { ConditionType, ISpecifyMission } from '@/editor/flowEditor/control/nodes/condition/const';
import { ISpineResult } from '@/material/form-upload/folder';
import { IQA } from '@/pages/bot/info/custom-form/customQA';
import { IRelationshipValue } from '@/pages/bot/info/custom-form/customRelationship';
import { CooperationType } from '@/pages/finance/contract/contractDraft/contractDraftInfo';
import { CardData } from '@/pages/mission/info/card/event/customBranch/Branch/numberSystem/cards';
import { IReplaceMsgTextInfo, ISchedule } from '@/store/modules/__endpoints__/module';

import { AiStyleInfo, Voice } from './aigc';
import { IGamePlay } from './gamePlay';
import { EAsyncStatus, FillType, MultiCharacter, RichMediaValue } from './media';

export interface IBasic {
  currTab: number;
  pageUniqueKey?: string;
}

export enum BotMode {
  BOT_MODE_DEFAULT = 0,
  BOT_MODE_IP
}

export interface IPhase {
  character: string;
  instruction?: string;
}

export interface IScoreRule {
  rangeMin: number;
  rangeMax: number;
  rule: string;
}

export enum PhaseSelectorType {
  PHASESELECTORTYPE_DEFAULT = 0,
  PHASESELECTORTYPE_RANGE,
  PHASESELECTORTYPE_KEYWORDS,
}

interface IRangePhaseSelector {
  type: PhaseSelectorType.PHASESELECTORTYPE_RANGE;
  rangeMin: number;
  rangeMax: number;
  phaseIndex: number;
}

interface IKeywordsPhaseSelector {
  type: PhaseSelectorType.PHASESELECTORTYPE_KEYWORDS;
  keywords: string[];
  phaseIndex: number;
}

export type IPhaseSelector = IRangePhaseSelector | IKeywordsPhaseSelector;

export interface IBot {
  modelType?: number;
  voiceType?: number;
  id: string;
  name: string;
  bgImage: RichMediaValue;
  intro: string;
  talking: string;
  status: AuditStatusType;
  detailStatus?: BotAuditStatusDetailType,
  mode: BotMode;
  chatBackground?: RichMediaValue;
  addUserBackground?: RichMediaValue,
  addUserForeBackground?: RichMediaValue;
  narrationType: NarrationType;
  closeUp: string;
  agentAge?: string;
  agentHobby?: string[];
  agentOccupation?: string[];
  prefix?: string;
  skillIds?: string[];
  reqBottomReply?: string[];
  rspBottomReply?: string[];
  voiceId?: string;
  voiceInfo?: Voice;
  emoticonIds?: string[];
  emoticonProbability?: number;
  modelId?: string;
  certification?: number;
  visibility?: Visibility;
  tags?: string[];
  knowledgeIds?: string[];
  ignoreMemory?: number;
  relatedId?: string;
  avatar?: RichMediaValue;
  initScore?: number;
  phasesNew?: IPhase[];
  scoreRules?: IScoreRule[];
  phaseSelectors: IPhaseSelector[]
  background?: string;
  agentTrait?: string[];
  agentLifetime?: string[];
  agentSocialRelation?: string[];
  agentSocialRelationNew?: IRelationshipValue[];
  userInstructionNew: { q: string; a: string }[];
  userInstruction?: string[];
  userInstructionBase?: string;
  agentUserRelation?: string;
  replaceMsgTextConf?: IReplaceMsgTextInfo[];
  materialMap: Record<string, string>;
  _ctime?: string;
  _mtime?: string;
  createdBy: string;
  updatedBy: string;
  publishedAt: string;
  updatedAt: string;
  prompt?: string;
  definitions?: IQA[];
  cover?: boolean;
  schedules?: ISchedule[];
  botDetail?: Partial<IBot>;
}

export enum GoalType {
  GOAL_TYPE_DEFAULT = 0,
  GOAL_TYPE_ASSISTANT_MESSAGE = 1, // 系统消息
  GOAL_TYPE_USER_MESSAGE = 2, // 用户消息
  GOAL_TYPE_CHAT_ROUND = 3, // 会话轮次
  GOAL_TYPE_USER_INTIMACY = 4, // 亲密度
  GOAL_TYPE_COUNTDOWN = 5, // 倒计时
  GOAL_TYPE_NUM_SYS = 6, // 数值系统
}

export enum CompletionTipType {
  COMPLETIONTYPE_DEFAULT = 0,
  COMPLETIONTYPE_TOBECONTINUED = 1,
  COMPLETIONTYPE_END = 2,
  COMPLETIONTYPE_SINGLEBRANCH = 3,
  COMPLETIONTYPE_MULTIBRANCH = 4,
  COMPLETIONTYPE_NEXTSCENE = 5,
}

export const CompletionTipValue = {
  [CompletionTipType.COMPLETIONTYPE_SINGLEBRANCH]: 'next',
  [CompletionTipType.COMPLETIONTYPE_END]: 'end',
  [CompletionTipType.COMPLETIONTYPE_TOBECONTINUED]: 'continued',
};

export interface IUsedBot {
  botId: string;
  missions: {
    id: string;
    name: string;
    type: MissionType;
  }[]; // 角色所在剧情
}

export interface IMission {
  id: string;
  name: string;
  clue?: string;
  isCard?: boolean;
  desc: string;
  image: RichMediaValue;
  imageBlur: RichMediaValue;
  imageBinary?: RichMediaValue;
  bgImage?: RichMediaValue;
  bgImageBlur?: RichMediaValue;
  isNecessary: boolean,
  missionEvent?: any[];
  relatedMessages?: any[];
  assetsEvent?: any[];
  assets: any[];
  isDraft?: boolean;
  missionType: MissionType;
  missionGoal: {
    assistantMessageGoal: { text: string };
    userMessageGoal: { text: string };
    chatRound: { round: number };
    sceneIntimacyGoal: { minScore: number, maxScore: number };
    countDown: { isOpen: boolean }
    goalType: GoalType;
    numSysGoal: {
      activationCond: CardData[]
    }
  },
  materialMap: Record<string, string>;
  completionTip?: {
    tipType?: CompletionTipType;
    tip?: string;
    title?: string;
    multiBranch?: Branch[];
    sortChange?: boolean;
    nonBranchNumCond?: {
      useOptionCond?: boolean;
      numChange?: CardData[];
    }
  };
  publishedAt?: string;
  canDelete?: boolean;
  foregroundImage?: RichMediaValue;
  version?: string;
}

export interface Branch {
  optionName?: string;
  sceneId?: string;
  sceneName?: string;
  missionId?: string;
  keyIndex?: number;
  useOptionCond?: boolean;
  optionCond?: {
    activationCond: CardData[];
    numChange: CardData[];
  }
  openCollapse?: boolean;
}

export interface IAssets {
  id?: string;
  name?: string;
  clue?: string;
  isCard?: boolean;
  desc?: string;
  bgImage?: RichMediaValue;
  bgImageBlur?: RichMediaValue;
  isNecessary?: undefined,
  assetsEvent?: any[];
  assets?: any[];
  isDraft?: boolean;
  materialMap?: Record<string, string>;
}

export interface ICard {
  id: string;
  cardId: string;
  bot_id: string;
  name: string;
  intro: string;
  rarity: number;
  bgImage: RichMediaValue;
  frontImage: string;
  materialMap: Record<string, string>;
  _ctime?: string;
  _mtime?: string;
  createdBy: string;
  updatedBy: string;
}

export enum StoryLayout {
  LAYOUT_VERTICAL = 1,
  LAYOUT_HORIZONTAL = 2,
}

export interface IStoryRes {
  id: string;
  storyId: string;
  name: string;
  subtitle: string;
  image?: RichMediaValue;
  backgroundUrl?: RichMediaValue;
  background?: string;
  intro: string;
  sceneList: IScene[];
  bots: IBot[];
  botPromptList: number[];
  playerInfo: Record<string, string>;
  materialMap: Record<string, string>;
  spaceId: string;
  _ctime?: string;
  _mtime?: string;
  createdBy: string;
  updatedBy: string;
}

export interface IPlayerInfo {
  name: string;
  avatar: RichMediaValue;
}

export enum Visibility {
  VISIBILITY_DEFAULT = 0,
  VISIBILITY_PUBLIC,
  VISIBILITY_SHARE,
  VISIBILITY_PRIVATE,
}

export interface IStoryChapter {
  story: IStory,
  chapter: IChapter;
  release: boolean;
}

export interface IStory {
  id: string;
  name: string;
  storyId: string;
  cooperationType?: CooperationType;
  subtitle: string;
  image?: RichMediaValue;
  backgroundUrl?: RichMediaValue;
  bgm?: RichMediaValue;
  background?: string;
  tagIds: string[];
  intro: string;
  chapterList?: IChapter[];
  sceneList: IScene[];
  botPromptList: Partial<IBot>[];
  hiddenBotIds?: string[]; // 隐藏的角色列表
  chatBotIds?: string[];// 可以私聊的角色列表 woa正式表也可以修改
  itemCardList?: IMission[]
  bots?: IBot[];
  playerInfo: IPlayerInfo;
  materialMap: Record<string, string>;
  spaceId: string;
  spaceDetail?: ISpace;
  _ctime?: string;
  _mtime?: string;
  createdBy: string;
  updatedBy: string;
  status?: AuditStatusType;
  payAuditStatus?: PayAuditStatusType;
  detailStatus?: AuditStatusDetailType;
  visibility?: Visibility;
  propsList?: any[];
  disabled?: boolean;
  publishedAt?: string;
  updatedAt?: string;
  version?: string;
  layout?: StoryLayout;
  fillType?: FillType;
  styleInfo?: AiStyleInfo;
  isSign?: boolean;// 是否已签约

  isOpenDub?: boolean; // 是否开启AI自动配音
  narrationDub?: Voice; // 旁白音色

  // 前端使用
  release?: boolean;
}

export interface IStoryFlow {
  id: string;
  name?: string;
  space?: string;
  intro?: string;
  playerInfo: IPlayerInfo;
  bots?: Partial<IBot>[];
  image?: RichMediaValue;
  isDraft?: boolean;
}

export interface ISceneFlow {
  id: string;
  storyId?: string;
  chapterId?: string;
  sceneType: SceneType;
  name?: string;
  guideReply?: boolean;
  isFullScreen?: boolean;
  narrationType?: NarrationType;
  bots?: Partial<IBot>[];
  image?: RichMediaValue;
  imageBinary?: RichMediaValue;
  isDraft?: boolean;
  background?: string;
  goal?: string;
  canDelete?: boolean;
  extendMission?: IMission;
  publishedAt?: string;
  isFinal?: boolean;
}

export interface IConditionFlow {
  id?: string;
  conditionNodeId: string;
  sceneId: string;
  rootId: string; // AddCondition节点id
  type: ConditionType; // 0：default 1：已废弃 2：过关条件 3：陷阱条件
  missions?: ISpecifyMission[];
  openTrigger?: boolean;
  openCount?: boolean;
  triggerValue?: {
    minV: number;
    maxV: number;
  }
  countDown?: number;
  triggerMissionId?: string;
  nextSceneId?: string;
  isEmpty?: boolean; // 区分新建状态，弹窗提交后置为false
  isDraft?: boolean; // 指定卡片为空或已被删除，提醒用户主动修改 || 未设置过关条件
  canDelete?: boolean;
}

export enum SceneType {
  SCENE_TYPE_DEFAULT = 0,
  SCENE_TYPE_NORMAL = 1, // AI幕
  SCENE_TYPE_FINAL = 2, // 结局幕
  SCENE_TYPE_OPTION = 3, // 选项幕
}

export enum SceneTab {
  BASE = 'base', // 本幕简介
  BOTS = 'bots', // 本幕角色
  GAME = 'game', // AI 玩法
  PROLOGUE = 'prologue', // 开场剧情
}

export enum FinalSceneType {
  SCENEENDINGTYPE_DEFAULT = 0,
  SCENEENDINGTYPE_ENDING = 1,
  SCENEENDINGTYPE_GAMEOVER = 2,
}

export enum NarrationType {
  NARRATION_TYPE_DEFAULT = 0,
  NARRATION_TYPE_1 = 1,
  NARRATION_TYPE_2 = 2,
}

export const NarrationValue = {
  [NarrationType.NARRATION_TYPE_DEFAULT]: '关闭',
  [NarrationType.NARRATION_TYPE_1]: '短续写',
  [NarrationType.NARRATION_TYPE_2]: '长续写',
};

export interface IScene {
  id?: string;
  sceneId: string;
  name: string;
  sceneType: SceneType;
  image?: RichMediaValue;
  imageBinary?: RichMediaValue;
  bgm?: RichMediaValue;
  botPromptList: Partial<IBot>[];
  background?: string;
  goal?: string;
  bots?: Partial<IBot>[];
  missions: IMission[];
  missionsData?: MissionIData[];
  goalsPlugins?: any;
  events?: string;
  showMainChar?: boolean;
  finalScene?: boolean;
  materialMap?: Record<string, string>;
  passConditions?: any[];
  countDown?: number;
  _ctime?: string;
  _mtime?: string;
  createdBy?: string;
  updatedBy?: string;
  // intimacy?: IIntimacy
  guideReply: boolean;
  isFullScreen?: boolean
  narrationType?: NarrationType;
  missionList?: {
    mission: IMission
  }[];
  storyId?: string;
  chapterId?: string;
  isDraft?: boolean;
  publishedAt?: string;
  canDelete?: boolean;
  extendMission?: IMission;
  isFirst?: boolean; // 是否是第一幕
  isFinal?: boolean; // 是否是结局幕
  aiGamePlay?: Record<string, any>;
  relateBots?: string[];
  autoSave?: boolean;
  version?: string;
  gamePlay?: IGamePlay;
}

export interface MissionIData {
  id?: string;
  name?: string;
  image?: RichMediaValue;
  clue?: string;
  imageBlur?: RichMediaValue;
  assets?: unknown[];
  isNecessary?: boolean;
  missionGoal?: IMission['missionGoal'],
  missionEvent?: IEvents[];
  assetsEvent?: IEvents[];
  relatedMessages?: IEvents[];
  materialMap?: Record<string, string>;
  completionTip?: {
    tipType?: CompletionTipType;
    tip?: string;
    multiBranch?: Branch[]
  }
  foregroundImage?: RichMediaValue;
  version?: string;
}

export interface PropsIData {
  id?: string;
  name?: string;
  bgImage?: RichMediaValue;
  bgImageBlur?: RichMediaValue;
  assets?: unknown[];
  isNecessary?: boolean;
  assetsEvent?: IEvents[];
  relatedMessages?: IEvents[];
  materialMap?: Record<string, string>;
}

export interface IEventMessageText {
  text: string,
  showType: 0,
  dubUrl?: RichMediaValue['url'],
  openWebDub?: boolean, // 前端用于判断是否需要打开旁白的音效UI，仅cms UI使用
  asyncLoading?: { asyncStatus?: EAsyncStatus }, // 处理异步上传loading
  volume?: number;
}

export interface IEvents {
  type: EventType; // 1: message, 2: action
  message?: {
    type: ContentType; // 1: text, 2: image, 3: audio, 4: video
    text?: IEventMessageText; // RichMediaShowType text use 0
    image?: RichMediaValue;
    audio?: RichMediaValue;
    video?: RichMediaValue;
    sender?: {
      type: SenderType, //  1: bot, 2: system, 3: user
      senderId: string; // botId
    }
  },
  action?: {
    type: ActionType; // use changeMedia 6
    changeMedia?: {
      operation?: ChangeMediaOperationType; // 1 修改 2 删除
      type: ChangeMediaType; // 1: backgroundImage背景图, 2:foregroundImage前景图, 3:audio, 4:video
      backgroundImage?: RichMediaValue;
      foregroundImage?: RichMediaValue;
      multiVerticalPainting?: MultiCharacter[];
      bgmAudio?: RichMediaValue;
      fullVideo?: RichMediaValue;
      backgroundVideo?: RichMediaValue;
      spine?: ISpineResult;
    },
    showAsset?: {
      type: AssetType,
      itemCard: IAssets;
    },
    guideReply?: {
      options?: string[]
    }
  },
  exclDialog?: boolean;
  keyIndex?: string; // 前端标志数组的唯一key
}

export interface ISpace {
  id: string;
  spaceId: string;
  name: string;
  stories?: string[];
  _ctime: string;
  _mtime: string;
  bannerUrl: string;
  materialMap: Record<string, string>;
  intro: string;
  coverUrl: RichMediaValue;
  createdBy: string;
  updateBy: string;
  storyList?: IStory[];
}

export interface Auth {
  id: string;
  vuid: string;
  type: AuthType;
  authKeys: string[]
}

export enum PricingModel {
  Free = 1,
  Fee,
}

export interface IChapter {
  id: string;
  storyId: string;
  name: string;
  version: number;
  pricingModel: PricingModel;
  price: string;
  createdBy: string;
  updatedAt: string;
  publishedAt: string;
  sceneList: IScene[];
  release: boolean;
}
